import React, { useState, useMemo } from 'react';
import {
  Users,
  Search,
  Filter,
  MapPin,
  Phone,
  Mail,
  Briefcase,
  Ban,
  RotateCcw,
  Eye,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

interface AdminCustomerSectionProps {
  customers: any[];
  onAction: (customerId: string, action: 'suspend' | 'reactivate', reason?: string) => Promise<void>;
  isProcessing: boolean;
}

export const AdminCustomerSection: React.FC<AdminCustomerSectionProps> = ({
  customers,
  onAction,
  isProcessing,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCust, setSelectedCust] = useState<any | null>(null);
  const [actionModal, setActionModal] = useState<{
    isOpen: boolean;
    customer: any | null;
    action: 'suspend' | 'reactivate';
  }>({
    isOpen: false,
    customer: null,
    action: 'suspend',
  });
  const [actionReason, setActionReason] = useState('');

  const states = useMemo(() => {
    const set = new Set<string>();
    customers.forEach((c) => {
      if (c.state) set.add(c.state);
    });
    return Array.from(set);
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      const name = (c.full_name || c.name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const phone = c.phone || '';
      const st = (c.state || '').toLowerCase();
      const term = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm ||
        name.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        c.id.toLowerCase().includes(term);

      const matchesState = stateFilter === 'all' || st === stateFilter.toLowerCase();

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && !c.isSuspended) ||
        (statusFilter === 'suspended' && c.isSuspended);

      return matchesSearch && matchesState && matchesStatus;
    });
  }, [customers, searchTerm, stateFilter, statusFilter]);

  const handleOpenActionModal = (customer: any, action: 'suspend' | 'reactivate') => {
    setActionModal({ isOpen: true, customer, action });
    setActionReason(
      action === 'suspend'
        ? 'Account suspended due to policy violation or abuse of platform terms.'
        : 'Account reactivated following administrative inquiry.'
    );
  };

  const handleConfirmAction = async () => {
    if (!actionModal.customer) return;
    await onAction(actionModal.customer.id, actionModal.action, actionReason);
    setActionModal({ isOpen: false, customer: null, action: 'suspend' });
    if (selectedCust?.id === actionModal.customer.id) {
      setSelectedCust(null);
    }
  };

  return (
    <div id="admin-customers-section" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-5 rounded-2xl">
        <div>
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span>Customer Registry ({customers.length})</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Monitor client activity, verify contact numbers, review job posting volumes, and manage account statuses.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-300 border border-blue-500/20 font-semibold">
            {customers.filter((c) => !c.isSuspended).length} Active
          </span>
          <span className="px-2.5 py-1 rounded-lg bg-rose-500/10 text-rose-300 border border-rose-500/20 font-semibold">
            {customers.filter((c) => c.isSuspended).length} Suspended
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by customer name, email, phone, or ID..."
            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* State */}
        <div>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-blue-500"
          >
            <option value="all">All States</option>
            {states.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Account Statuses</option>
            <option value="active">Active Accounts</option>
            <option value="suspended">Suspended Accounts</option>
          </select>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950/60 border-b border-slate-800 text-[11px] uppercase font-bold text-slate-400 tracking-wider">
              <tr>
                <th className="py-3.5 px-4">Customer Name & ID</th>
                <th className="py-3.5 px-4">Contact Information</th>
                <th className="py-3.5 px-4">Location</th>
                <th className="py-3.5 px-4">Job Requests</th>
                <th className="py-3.5 px-4">Account Status</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    No customers found matching the search criteria.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((cust) => {
                  const isSuspended = Boolean(cust.isSuspended);

                  return (
                    <tr
                      key={cust.id}
                      className={`hover:bg-slate-800/40 transition-colors ${
                        isSuspended ? 'bg-rose-950/10' : ''
                      }`}
                    >
                      {/* Name & Avatar */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center justify-center font-bold text-xs flex-shrink-0">
                            {cust.full_name?.charAt(0) || 'C'}
                          </div>
                          <div>
                            <div className="font-bold text-white flex items-center gap-2">
                              <span>{cust.full_name}</span>
                              {isSuspended && (
                                <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">
                                  SUSPENDED
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                              ID: {cust.id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5 text-[11px]">
                          <div className="text-slate-300 flex items-center gap-1.5">
                            <Mail className="w-3 h-3 text-slate-500" />
                            <span>{cust.email || 'No email provided'}</span>
                          </div>
                          <div className="text-slate-400 flex items-center gap-1.5 font-mono">
                            <Phone className="w-3 h-3 text-slate-500" />
                            <span>{cust.phone || 'No phone'}</span>
                          </div>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="py-3.5 px-4">
                        <div className="text-[11px] text-slate-300 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-500" />
                          <span>{cust.lga ? `${cust.lga}, ${cust.state}` : cust.state || 'Nigeria'}</span>
                        </div>
                      </td>

                      {/* Job Requests */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5 text-[11px]">
                          <span className="font-semibold text-white">
                            {cust.jobsPostedCount || 3} Jobs Posted
                          </span>
                          <div className="text-slate-400">
                            {cust.completedJobsCount || 1} completed
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${
                            isSuspended
                              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                              : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          }`}
                        >
                          {isSuspended ? (
                            <>
                              <AlertTriangle className="w-3 h-3" />
                              Suspended
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-3 h-3" />
                              Active
                            </>
                          )}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedCust(cust)}
                            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                            title="Inspect Customer Activity"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>

                          {isSuspended ? (
                            <button
                              onClick={() => handleOpenActionModal(cust, 'reactivate')}
                              className="px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500 text-blue-300 hover:text-white rounded-lg text-xs font-bold border border-blue-500/30 transition-all"
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleOpenActionModal(cust, 'suspend')}
                              className="p-1.5 bg-slate-800 hover:bg-rose-900/40 text-slate-400 hover:text-rose-300 rounded-lg transition-colors"
                              title="Suspend Customer Account"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Profile Modal */}
      {selectedCust && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-lg shadow-2xl p-6 sm:p-8 space-y-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/20 text-blue-300 flex items-center justify-center font-bold text-lg">
                  {selectedCust.full_name?.charAt(0) || 'C'}
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{selectedCust.full_name}</h3>
                  <p className="text-xs text-slate-400 font-mono">ID: {selectedCust.id}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCust(null)}
                className="p-1.5 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-2.5 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">Email:</span>
                <span className="text-white font-medium">{selectedCust.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Phone Number:</span>
                <span className="text-white font-mono">{selectedCust.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">State & LGA:</span>
                <span className="text-white">{selectedCust.lga || 'Ikeja'}, {selectedCust.state || 'Lagos'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Account Type:</span>
                <span className="text-blue-300 font-bold uppercase">Customer</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                onClick={() => setSelectedCust(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend / Reactivate Confirmation Modal */}
      {actionModal.isOpen && actionModal.customer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h3 className="text-base font-bold text-white capitalize flex items-center gap-2">
              {actionModal.action === 'suspend' ? (
                <Ban className="w-5 h-5 text-rose-400" />
              ) : (
                <RotateCcw className="w-5 h-5 text-blue-400" />
              )}
              <span>{actionModal.action} Customer: {actionModal.customer.full_name}</span>
            </h3>

            <p className="text-xs text-slate-300">
              {actionModal.action === 'suspend'
                ? 'Suspending this customer prevents them from posting new job requests and initiating WhatsApp AI workflows.'
                : 'Reactivating this customer account restores full job posting access.'}
            </p>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Reason / Administrative Justification:
              </label>
              <textarea
                rows={3}
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter justification..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setActionModal({ isOpen: false, customer: null, action: 'suspend' })}
                disabled={isProcessing}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={isProcessing || !actionReason.trim()}
                className={`px-4 py-2 text-xs font-bold rounded-xl transition-colors disabled:opacity-50 ${
                  actionModal.action === 'suspend'
                    ? 'bg-rose-500 hover:bg-rose-600 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {isProcessing ? 'Processing...' : `Confirm ${actionModal.action.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
